import type { ApiError } from './types';

/**
 * The HTTP client.
 *
 * Two things here are security decisions rather than plumbing, and both are the
 * kind that get "simplified" by someone who does not know why they are there:
 *
 *   THE ACCESS TOKEN LIVES IN A MODULE VARIABLE, NOT IN STORAGE.
 *   localStorage and sessionStorage are readable by any script that runs on this
 *   origin. One compromised dependency, one injected script, and a bearer token for
 *   a business that handles money is exfiltrated silently. A module variable dies
 *   with the tab and cannot be read by a script that is not already running inside
 *   our own bundle.
 *
 *   ONE REFRESH AT A TIME.
 *   Refresh tokens ROTATE, and the API detects reuse by revoking the whole family
 *   (T-125). So two requests racing on an expired token would send the same refresh
 *   token twice, and the server would correctly read that as theft and sign the user
 *   out of everything. The single in-flight promise below is not an optimisation; it
 *   is what stops the app logging itself out under load.
 */

const BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:5080';

/**
 * An API path as an absolute address the BROWSER can navigate to - for the few
 * responses the browser itself must receive (a Wallet pass), rather than fetch().
 */
export function apiUrl(path: string): string {
  return `${BASE_URL}${path}`;
}

let accessToken: string | null = null;
let refreshInFlight: Promise<boolean> | null = null;

/** Called when the session cannot be recovered. Set once, by the auth provider. */
let onSessionLost: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setSessionLostHandler(handler: () => void): void {
  onSessionLost = handler;
}

/** Thrown for every non-2xx. Carries the API's error envelope when there is one. */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly traceId: string | undefined;
  readonly fieldErrors: Record<string, string[]> | undefined;

  constructor(status: number, body: Partial<ApiError> | null) {
    super(body?.message ?? 'Something went wrong. Please try again.');
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = body?.code ?? 'UNKNOWN';
    this.traceId = body?.traceId;
    this.fieldErrors = body?.errors;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Required by the API on anything that moves money. */
  idempotencyKey?: string;
  signal?: AbortSignal;
  /** Internal: prevents a refresh attempt from recursing into itself. */
  skipRefresh?: boolean;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, idempotencyKey, signal, skipRefresh = false } = options;

  const headers: Record<string, string> = { Accept: 'application/json' };

  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (accessToken !== null) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }
  if (idempotencyKey !== undefined) {
    headers['Idempotency-Key'] = idempotencyKey;
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    body: body === undefined ? null : JSON.stringify(body),
    // No cookies are used, so no CSRF surface: the credential is a header the
    // browser will not attach on its own to a cross-site request.
    credentials: 'omit',
    ...(signal ? { signal } : {}),
  });

  if (response.status === 401 && !skipRefresh) {
    const recovered = await refreshOnce();

    if (recovered) {
      return request<T>(path, { ...options, skipRefresh: true });
    }

    onSessionLost?.();
    throw new ApiRequestError(401, { code: 'SESSION_EXPIRED', message: 'Your session has ended. Please sign in again.' });
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const payload = await readJson(response);

  if (!response.ok) {
    throw new ApiRequestError(response.status, payload as Partial<ApiError> | null);
  }

  return payload as T;
}

async function readJson(response: Response): Promise<unknown> {
  const text = await response.text();

  if (text.length === 0) {
    return null;
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    // A proxy error page, or a gateway timeout in HTML. Not something to surface.
    return null;
  }
}

/**
 * Refreshes at most once at a time. Concurrent callers await the same attempt —
 * see the note at the top of this file about reuse detection.
 */
function refreshOnce(): Promise<boolean> {
  refreshInFlight ??= doRefresh().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

async function doRefresh(): Promise<boolean> {
  const stored = readRefreshToken();

  if (stored === null) {
    return false;
  }

  try {
    const tokens = await request<{ accessToken: string; refreshToken: string }>(
      '/api/v1/auth/refresh',
      { method: 'POST', body: { refreshToken: stored }, skipRefresh: true },
    );

    accessToken = tokens.accessToken;
    writeRefreshToken(tokens.refreshToken);
    return true;
  } catch {
    // Expired, revoked, or the family was killed by reuse detection. Either way the
    // session is gone; clear it rather than retrying into a revoked family.
    clearRefreshToken();
    return false;
  }
}

/* -------------------------------------------------------------------------- */
/* Refresh token storage                                                       */
/*                                                                             */
/* sessionStorage, not localStorage: it is scoped to this tab and cleared when  */
/* the tab closes, so a shared or public machine does not keep a live session   */
/* after the person walks away.                                                */
/*                                                                             */
/* It is still readable by script on this origin, and that is a real limitation.*/
/* The right fix is server-side — an httpOnly, Secure, SameSite=Strict cookie,  */
/* which script cannot read at all. That needs an API change; until then,       */
/* rotation with reuse detection is what bounds the damage: a stolen token       */
/* becomes useless the moment either party uses it twice.                       */
/* -------------------------------------------------------------------------- */

const REFRESH_KEY = 'nf.rt';

export function readRefreshToken(): string | null {
  try {
    return window.sessionStorage.getItem(REFRESH_KEY);
  } catch {
    // Storage disabled, or a privacy mode that throws on access.
    return null;
  }
}

export function writeRefreshToken(token: string): void {
  try {
    window.sessionStorage.setItem(REFRESH_KEY, token);
  } catch {
    // The session then lasts only as long as the page. Degraded, not broken.
  }
}

export function clearRefreshToken(): void {
  try {
    window.sessionStorage.removeItem(REFRESH_KEY);
  } catch {
    /* nothing to clear */
  }
}
